'use client';
import React, { useEffect, useState } from 'react';
import { siteSettings } from '@/data/siteData';

export default function HeroSection() {
  const [totalProjects, setTotalProjects] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [profileImage, setProfileImage] = useState<string>('');
  const [heroName, setHeroName] = useState<string>(siteSettings?.name);
  const [tagline, setTagline] = useState<string>(siteSettings?.tagline);
  const [description, setDescription] = useState<string>(
    Array.isArray(siteSettings?.aboutText) ? siteSettings?.aboutText?.[0] : ''
  );
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  useEffect(() => {
    setMounted(true);
    fetch('/api/portfolio')?.then((res) => res?.json())?.then((data) => {
        const items = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
        setTotalProjects(items?.length ?? 0);
      })?.catch(() => {
        setTotalProjects(siteSettings?.totalProjects ?? 0);
      });

    fetch('/api/settings')?.then((res) => res?.json())?.then((data) => {
        const s = data?.settings;
        if (s?.profileImage) setProfileImage(s?.profileImage);
        if (s?.name) setHeroName(s?.name);
        if (s?.tagline) setTagline(s?.tagline);
        if (s?.aboutText) {
          const text = Array.isArray(s?.aboutText) ? s?.aboutText?.[0] : s?.aboutText;
          if (text) setDescription(text);
        }
      })?.catch(() => {})
      ?.finally(() => setImageLoading(false));
  }, []);

  return (
    <div className="m-10 flex gap-4 flex-col-reverse md:flex-row">
      {/* Left content */}
      <div className="flex flex-col gap-4 animate__animated animate__bounceInDown">
        <p className="font-semibold">Hallo, Saya {heroName}</p>
        <h1 className="text-4xl font-semibold text-gray-600">{tagline}</h1>
        <p>{description}</p>

        {/* Stats card */}
        <div className="max-w-[16rem]">
          <div className="flex flex-row m-auto p-2 gap-8 rounded-lg border bg-white bg-opacity-20">
            <div className="my-auto">
              <div className="text-lg text-gray-400">Total Project</div>
              <div className="text-4xl text-gray-400" suppressHydrationWarning>
                {!mounted || totalProjects === null ? (
                  <span className="inline-block w-10 h-9 bg-gray-200 animate-pulse rounded" />
                ) : (
                  totalProjects
                )}
              </div>
            </div>
            <div className="text-gray-400 my-auto bg-gray-500 bg-opacity-20 rounded-full p-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
              </svg>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex gap-4 flex-wrap">
          <a href="/#portfolio">
            <div
              className="h-10 w-28 rounded-md bg p-[2px]"
              style={{ background: 'linear-gradient(90deg, #74A9F5, #E7C2F9 65%)' }}
            >
              <div className="flex h-full w-full items-center justify-center rounded-md bg-white">
                <span
                  className="font-semibold text-transparent bg-clip-text"
                  style={{ backgroundImage: 'linear-gradient(90deg, #74A9F5, #E7C2F9 65%)' }}
                >
                  Portfolio
                </span>
              </div>
            </div>
          </a>
          <a href="#contact">
            <div
              className="h-10 w-28 flex items-center justify-center border rounded-md text-white font-semibold"
              style={{ background: 'linear-gradient(90deg, #74A9F5, #E7C2F9 65%)' }}
            >
              Let&apos;s Talk !
            </div>
          </a>
        </div>
      </div>

      {/* Right - Profile Image */}
      <div className="w-full flex items-center justify-center md:justify-end animate__animated animate__fadeInRight">
        <div
          className="rounded-full w-56 h-56 overflow-hidden flex items-center justify-center"
          style={{ background: 'linear-gradient(90deg, #74A9F5, #E7C2F9 65%)' }}
        >
          {imageLoading ? (
            <div className="w-full h-full bg-gray-200 animate-pulse rounded-full" />
          ) : profileImage ? (
            <img
              src={profileImage}
              alt={`${heroName} - Full Stack Developer`}
              className="w-full object-cover object-top"
              style={{ height: '340px', marginTop: '-70px' }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
